import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DefaultButton,
  DefaultPrimaryButton,
  InputError,
  InputWithLabel,
  SelectBox,
} from "../../components/micro";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Breadcrumb from "../../components/common/Breadcrumb";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { createAudio, editAudio } from "../../redux/audio/slice";
import { useDispatch } from "react-redux";
import { uploadImage } from "../../redux/uploadFile/slice";
import { toast } from "react-toastify";
import RippleLoader from "../../components/common/RippleLoader";
import FileUploadChoose from "../../components/common/FileUploadChoose";
import { FileUpload } from "../../assets/svg";

const AddEditAudio = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [file, setFile] = useState(null);
  const [fileURL, setFileURL] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const audioData = location?.state?.audio;
  const isEditMode = location?.state?.mode;

  useEffect(() => {
    if (audioData?.audioUrl && isEditMode === "edit") {
      setFileURL(audioData.audioUrl);
    }
  }, [audioData, isEditMode]);

  const handleEditClick = () => setIsEditing(true);
  const handleCancelClick = () => navigate("/audio");

  const audioInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleAudioChange = async (
    event,
    setFieldValue,
    droppedFile = null
  ) => {
    const files = droppedFile
      ? [droppedFile]
      : Array.from(event.target.files || []);
    if (!files.length) return;

    const temp = files[0];

    //  Reject non-audio files
    if (!temp.type.startsWith("audio/")) {
      toast.error("Only audio files are allowed (MP3, WAV, OGG, etc.)");
      if (!droppedFile && event?.target) event.target.value = "";
      return;
    }

    const localUrl = URL.createObjectURL(temp);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", temp);
      const res = await dispatch(uploadImage(formData));

      if (uploadImage.fulfilled.match(res)) {
        const { url, key } = res.payload.data;

        setFile(temp);
        setFileURL(localUrl);
        setFieldValue("audio", url);
        setFieldValue("audioKey", key);

        //  Extract duration
        const audioElement = document.createElement("audio");
        audioElement.src = localUrl;
        audioElement.addEventListener("loadedmetadata", () => {
          const duration = audioElement.duration;
          const minutes = String(Math.floor(duration / 60)).padStart(2, "0");
          const seconds = String(Math.floor(duration % 60)).padStart(2, "0");
          setFieldValue("audioDuration", `${minutes}:${seconds}`);
        });
      } else {
        toast.error("Audio upload failed. Please try again.");
        setFile(null);
        setFileURL("");
        setFieldValue("audio", "");
        setFieldValue("audioKey", "");
      }
    } catch (e) {
      toast.error("Audio upload failed. Please try again.");
      setFile(null);
      setFileURL("");
      setFieldValue("audio", "");
      setFieldValue("audioKey", "");
    } finally {
      setIsUploading(false);
    }
  };

  // Drag-and-drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    if (isEditMode === "edit" && !isEditing) return;
    setDragActive(true);
  };
  const handleDragLeave = () => setDragActive(false);

  const handleDrop = async (e, setFieldValue) => {
    e.preventDefault();
    setDragActive(false);
    if (isEditMode === "edit" && !isEditing) return;

    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;

    const temp = files[0];

    //  Reject non-audio files on drop
    if (!temp.type.startsWith("audio/")) {
      toast.error("Only audio files are allowed (MP3, WAV, OGG, etc.)");
      return;
    }

    await handleAudioChange(null, setFieldValue, temp);
  };

  const handleFileChange = async (event, setFieldValue, type, index = null) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const temp = files[0];
    const localUrl = URL.createObjectURL(temp);

    if (type === "coverimage") {
      setFieldValue("image", localUrl);
    }

    setIsUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", temp);

      const res = await dispatch(uploadImage(formData));
      if (uploadImage.fulfilled.match(res)) {
        const { url, key } = res.payload.data;
        if (type === "coverimage") {
          setFieldValue("image", url);
          setFieldValue("imageKey", key);
        }
      } else {
        toast.error("Upload failed. Please try again.");
      }
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleConfirm = () => {
    setIsLoading(true);
    const audioKey = formValues?.audioKey || formValues?.audioUrlKey || "";
    const audioUrl = audioKey || formValues?.audio || "";

    if (!audioUrl) {
      setIsLoading(false);
      return;
    }

    const payload = {
      audioName: formValues?.name,
      title: formValues?.title,
      description: formValues?.description,
      audioUrl: audioUrl,
      audioduration: formValues?.audioDuration,
      category: formValues?.category,
      image: formValues?.imageKey,
    };

    const action = audioData?._id
      ? editAudio({ id: audioData._id, ...payload })
      : createAudio(payload);

    dispatch(action)
      .then((res) => {
        if (
          res.type === "createAudio/fulfilled" ||
          res.type === "editAudio/fulfilled"
        ) {
          navigate("/audio");
        }
      })
      .finally(() => {
        setIsLoading(false);
        setShowConfirmationModal(false);
      });
  };

  const categoryOptions = [
    { value: "Better Sleep", label: "Better Sleep" },
    { value: "Meditation", label: "Meditation" },
  ];

  return (
    <>
      <div className="space-y-5">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">
            {isEditMode === "edit" ? "Edit Audio" : "Create Audio"}
          </h6>
          <Breadcrumb
            breadCrumbParent={"Home"}
            breadCrumbParent2={"Audio"}
            breadCrumbActive={
              isEditMode === "edit" ? "Edit Audio" : "Create Audio"
            }
            breadCrumbTitleKey={"/"}
          />
        </div>

        <div className="bg-white shadow-md rounded-lg p-6">
          <Formik
            initialValues={{
              name: audioData?.audioName || "",
              title: audioData?.title || "",
              description: audioData?.description || "",
              audio: audioData?.audioUrl || "",
              audioKey: audioData?.audioUrlKey || "",
              category: audioData?.category || "",
              image: audioData?.image || "",
              imageKey: audioData?.imageKey || "",
            }}
            validationSchema={Yup.object().shape({
              name: Yup.string()
                .trim()
                .nullable()
                .required("Please enter Audio name"),
              title: Yup.string()
                .trim()
                .nullable()
                .required("Please enter title"),
              description: Yup.string()
                .trim()
                .nullable()
                .required("Please enter description"),
              audio: Yup.string().required("Please upload an audio file"),
              category: Yup.string().required("Please enter category"),
              image: Yup.mixed().required("Image is required"),
            })}
            onSubmit={(values) => {
              setFormValues(values);
              setShowConfirmationModal(true);
            }}
          >
            {({
              handleChange,
              handleBlur,
              values,
              errors,
              touched,
              setFieldValue,
            }) => (
              <Form>
                <div className="grid grid-cols-2 gap-6 mb-4">
                  <InputWithLabel
                    idFromName="name"
                    name="name"
                    label="Audio Name*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    errorsText={errors.name}
                    errors={errors.name && touched.name}
                    className={`${
                      errors?.name && touched?.name
                        ? "border border-red"
                        : "border border-borderTheme/80 focus-within:border-themeBtn"
                    } w-full`}
                    disabled={isEditMode === "edit" && !isEditing}
                  />
                  <InputWithLabel
                    idFromName="title"
                    name="title"
                    label="Title*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.title}
                    errorsText={errors.title}
                    errors={errors.title && touched.title}
                    className={`${
                      errors?.title && touched?.title
                        ? "border border-red"
                        : "border border-borderTheme/80 focus-within:border-themeBtn"
                    } w-full`}
                    disabled={isEditMode === "edit" && !isEditing}
                  />
                  <SelectBox
                    name="category"
                    label="Category*"
                    placeholder="Select Category*"
                    options={categoryOptions}
                    onMenuOpen={true}
                    value={
                      categoryOptions.find(
                        (opt) => opt.value === values.category
                      ) || null
                    }
                    onChange={(option) => {
                      setFieldValue("category", option?.value ?? "");
                    }}
                    isDisabled={isEditMode === "edit" && !isEditing}
                    errors={errors.category && touched.category}
                    errorsText={errors.category}
                  />
                  <InputWithLabel
                    idFromName="description"
                    name="description"
                    label="Description*"
                    type="textarea"
                    rows={1}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.description}
                    errorsText={errors.description}
                    disabled={isEditMode === "edit" && !isEditing}
                    errors={errors.description && touched.description}
                    className={`${
                      errors?.description && touched?.description
                        ? "border border-red"
                        : "border border-borderTheme/80 focus-within:border-themeBtn"
                    } w-full`}
                  />
                </div>
                {/* Audio Upload */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">
                    Upload Audio *
                  </label>
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, setFieldValue)}
                    className={`relative p-2 rounded border transition-colors duration-200 ${
                      errors.audio && touched.audio
                        ? "border-red"
                        : dragActive
                        ? "border-themeBtn bg-themeBtn/5"
                        : "border-borderTheme/80"
                    } ${
                      isEditMode === "edit" && !isEditing
                        ? "bg-zinc-300/20 border-dashed cursor-not-allowed"
                        : "bg-white hover:border-themeBtn"
                    }`}
                  >
                    {/* Invisible input */}
                    <input
                      ref={audioInputRef}
                      type="file"
                      accept="audio/*"
                      disabled={isEditMode === "edit" && !isEditing}
                      className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                      onBlur={handleBlur}
                      onChange={(e) => handleAudioChange(e, setFieldValue)}
                    />

                    {/* Show loader while uploading */}
                    {isUploading ? (
                      <div className="py-3">
                        <RippleLoader />
                      </div>
                    ) : fileURL || values.audio ? (
                      <div className="mt-3 pt-2 relative">
                        <div className="relative">
                          <audio
                            controls
                            src={fileURL || values.audio}
                            className="w-full opacity-70"
                          />

                          {isEditMode === "edit" && !isEditing && (
                            <div className="absolute inset-0 bg-transparent cursor-not-allowed z-10"></div>
                          )}
                        </div>
                        {!(isEditMode === "edit" && !isEditing) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFile(null);
                              setFileURL("");
                              setFieldValue("audio", "");
                              setFieldValue("audioKey", "");
                              if (audioInputRef.current) {
                                audioInputRef.current.value = "";
                              }
                            }}
                            className="absolute z-50 p-2 -top-5 right-0 bg-gray-100 hover:bg-gray-300 text-gray-700 rounded-full"
                            title="Remove audio"
                          >
                            ✖
                          </button>
                        )}
                      </div>
                    ) : (
                      // Placeholder
                      <div className="flex flex-col items-center justify-center py-2 text-center text-gray-500 pointer-events-none select-none">
                        <FileUpload width={40} height={40} className="mb-2" />
                        <p className="text-sm">
                          Drag and Drop or choose your file for upload
                        </p>
                        <p className="text-sm text-textSecondary">
                          Upload Audio File{" "}
                        </p>
                      </div>
                    )}
                  </div>

                  {errors.audio && touched.audio && (
                    <InputError errorTitle={errors.audio} />
                  )}
                </div>

                {/* Cover Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Cover Image*
                  </label>
                  <FileUploadChoose
                    className={`border border-borderTheme/80 ${
                      errors.image && touched.image ? "border-red" : ""
                    } ${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border-dashed"
                        : ""
                    }`}
                    disabled={isEditMode === "edit" && !isEditing}
                    uploadedFiles={
                      values.imageKey ? [{ url: values.image }] : [] // only after API success
                    }
                    handleFileChange={(e) =>
                      handleFileChange(e, setFieldValue, "coverimage")
                    }
                    onDelete={() => {
                      setFieldValue("image", null);
                      setFieldValue("imageKey", null); // reset upload success
                    }}
                    accept="image/*"
                    filemsg="Upload JPG, PNG, JPEG file"
                  />

                  {errors.image && touched.image && (
                    <InputError errorTitle={errors.image} />
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-3">
                  <DefaultPrimaryButton
                    type="button"
                    value={isEditMode === "edit" ? "Back" : "Cancel"}
                    onClick={handleCancelClick}
                  />
                  {isEditMode === "edit" ? (
                    isEditing ? (
                      <>
                        <DefaultButton
                          type="submit"
                          value="Save"
                          onClick={(e) => {}}
                        />
                      </>
                    ) : (
                      <DefaultButton
                        value="Edit"
                        onClick={handleEditClick}
                        type="submit"
                      />
                    )
                  ) : (
                    <>
                      <DefaultButton type="submit" value="confirm" />
                    </>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {showConfirmationModal && (
        <ConfirmationModel
          confirmText={isEditMode === "edit" ? "Edit" : "Create"}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={handleConfirm}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default AddEditAudio;
